/**
 * FundingCreditsTable
 * Tabla compartida para listados de creditos en pantallas de fondeo (Partner y Fiadosa).
 *
 * Orden de columnas: Fecha · Monto · Nivel · Credit ID · Nombre · Telefono · Directory ID.
 * Credit ID y Directory ID son copiables.
 * Nombre, Nivel y Telefono muestran "PENDIENTE" en ambar cuando vienen vacios (ver PENDIENTES.md item 004).
 */

import { useMemo, useState } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { CopyButton } from '@/components/ui/CopyButton';
import { PendienteCell } from '@/components/data-display/PendienteCell';
import { CreditLevelBadge } from '@/features/credit/components/CreditLevelBadge';
import { formatCurrency } from '@/utils/formatters';
import { CreditLevel } from '@/types/credit.types';

export interface FundingCreditItem {
  credit_id: string;
  directory_id: string;
  amount_mxn: number;
  disbursed_at?: string | undefined;
  phone_number?: string | null | undefined;
  credit_level?: string | null | undefined;
  full_name?: string | null | undefined;
}

type SortKey =
  | 'disbursed_at'
  | 'amount_mxn'
  | 'credit_level'
  | 'credit_id'
  | 'full_name'
  | 'phone_number'
  | 'directory_id';
type SortDir = 'asc' | 'desc';

interface Props {
  credits: FundingCreditItem[];
  title: string;
  /** Bloque opcional de informacion (referencia bancaria, remitente, etc.). */
  info?: { bank_reference?: string | undefined; sent_by?: string | undefined } | undefined;
  /** Mensaje cuando no hay creditos. Default: "Sin creditos" */
  emptyMessage?: string;
}

function formatDateShort(dateStr: string): string {
  if (!dateStr) return '-';
  const date = new Date(dateStr);
  return new Intl.DateTimeFormat('es-MX', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date);
}

export function FundingCreditsTable({ credits, title, info, emptyMessage = 'Sin creditos' }: Props) {
  const [sortKey, setSortKey] = useState<SortKey | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  const sorted = useMemo(() => {
    if (!sortKey) return credits;
    return [...credits].sort((a, b) => {
      const valA = (a as unknown as Record<string, unknown>)[sortKey];
      const valB = (b as unknown as Record<string, unknown>)[sortKey];
      if (typeof valA === 'number' && typeof valB === 'number') {
        return sortDir === 'asc' ? valA - valB : valB - valA;
      }
      return sortDir === 'asc'
        ? String(valA ?? '').localeCompare(String(valB ?? ''))
        : String(valB ?? '').localeCompare(String(valA ?? ''));
    });
  }, [credits, sortKey, sortDir]);

  if (credits.length === 0) {
    return <p className="py-12 text-center text-sm text-muted-foreground">{emptyMessage}</p>;
  }

  const total = credits.reduce((s, c) => s + c.amount_mxn, 0);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ArrowUpDown size={10} className="opacity-30" />;
    return sortDir === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />;
  };

  const headerBtn = (col: SortKey, label: string, alignRight = false) => (
    <button
      type="button"
      className={`flex items-center gap-1 hover:text-foreground ${alignRight ? 'justify-end ml-auto' : ''}`}
      onClick={() => handleSort(col)}
    >
      {label} <SortIcon col={col} />
    </button>
  );

  return (
    <div>
      <div className="border-b border-border bg-muted/30 px-3 py-2">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {title}
          <span className="ml-2 normal-case tracking-normal font-normal">
            — {credits.length} creditos · {formatCurrency(total, 'MXN', 'es-MX')}
          </span>
        </h3>
        {info?.bank_reference && (
          <p className="mt-1 text-xs text-muted-foreground">
            Ref: <span className="font-mono text-foreground">{info.bank_reference}</span>
            {info.sent_by && <span className="ml-2">por {info.sent_by}</span>}
          </p>
        )}
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-[10px] uppercase tracking-wider text-muted-foreground">
            <th className="px-3 py-1.5 font-medium">{headerBtn('disbursed_at', 'Fecha')}</th>
            <th className="px-2 py-1.5 text-right font-medium">{headerBtn('amount_mxn', 'Monto', true)}</th>
            <th className="px-2 py-1.5 font-medium">{headerBtn('credit_level', 'Nivel')}</th>
            <th className="px-2 py-1.5 font-medium">{headerBtn('credit_id', 'Credit ID')}</th>
            <th className="px-2 py-1.5 font-medium">{headerBtn('full_name', 'Nombre')}</th>
            <th className="px-2 py-1.5 font-medium">{headerBtn('phone_number', 'Telefono')}</th>
            <th className="px-2 py-1.5 font-medium">{headerBtn('directory_id', 'Directory ID')}</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((c, idx) => {
            const levelValid =
              c.credit_level && Object.values(CreditLevel).includes(c.credit_level as CreditLevel);
            return (
              <tr key={c.credit_id ?? idx} className="border-b border-border/20" style={{ height: 32 }}>
                <td className="px-3 py-0 text-xs text-foreground">
                  {c.disbursed_at
                    ? formatDateShort(c.disbursed_at.split('T')[0] ?? c.disbursed_at)
                    : '-'}
                </td>
                <td className="px-2 py-0 text-right tabular-nums text-xs font-semibold text-foreground">
                  {formatCurrency(c.amount_mxn, 'MXN', 'es-MX')}
                </td>
                <td className="px-2 py-0 text-xs">
                  {levelValid ? (
                    <CreditLevelBadge level={c.credit_level as CreditLevel} size="sm" />
                  ) : (
                    <PendienteCell />
                  )}
                </td>
                <td className="px-2 py-0 text-xs">
                  {c.credit_id ? (
                    <CopyButton value={c.credit_id} label="Credit ID" variant="plain" />
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </td>
                <td className="px-2 py-0 text-xs">
                  {c.full_name ? (
                    <span className="text-foreground">{c.full_name}</span>
                  ) : (
                    <PendienteCell />
                  )}
                </td>
                <td className="px-2 py-0 text-xs">
                  {c.phone_number ? (
                    <span className="tabular-nums text-foreground">{c.phone_number}</span>
                  ) : (
                    <PendienteCell />
                  )}
                </td>
                <td className="px-2 py-0 text-xs">
                  {c.directory_id ? (
                    <CopyButton value={c.directory_id} label="Directory ID" variant="plain" />
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-border">
            <td className="px-3 py-1.5 text-xs font-semibold text-foreground">Total</td>
            <td className="px-2 py-1.5 text-right tabular-nums text-sm font-bold text-foreground">
              {formatCurrency(total, 'MXN', 'es-MX')}
            </td>
            <td colSpan={5} />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}
